import * as R from 'ramda';
import { FILTER_DIMENSIONS, UNBLOCKED_FRAUD_DATASOURCES } from '../../eo_mapping';
import type { Row, Rows } from '../../../../main/types/tableTypes';
import { reduceObj } from '../../../../main/support/utils';
import { serialize } from '../../../../common/support/utils';
import {
  STANDARD_DATASOURCE_TRANSFORMERS,
  STANDARD_BLOCKED_DATASOURCE_TRANSFORMERS,
  COST_TRANSFORMER,
} from './transformers';
import {
  MULTIPLE_CAMPAIGNS_ASSOCIATIONS,
  EO_DATASOURCES,
} from '../../../../main/screens/ScreenSKAdNetwork/ScreenSKAdNetworkConstants';
import appendNumericMetricWithEventData from './appendNumericMetricWithEventData';
import type { GroupByResponse, GroupByResult } from '../../../../main/types/druidTypes';
const UNPOPULATED_VALUE = 'Unpopulated';

/* GROUPBY */

/**
 * check to see if specified dimensions and params match event
 * @param {Object} template
 * {
 *   "user_data_platform": "ANDROID_APP",
 *   "from_desktop": "false"
 * }
 * @param {Object} datum
 * {
 *   "version": "v1",
 *   "timestamp": "2017-04-27T07:00:00.000Z",
 *   "event": {
 *     "user_data_platform": "IOS_APP",
 *     "from_desktop": "false",
 *     "total_count": 569
 *   }
 * }
 * @return {Boolean}
 */
export function checkDimensionMatch(template: Record<string, any>, datum: Record<string, any>): boolean {
  const event = datum.event;

  if (!event) {
    return false;
  }

  for (const key in template) {
    if (template.hasOwnProperty(key)) {
      const valueInEvent = event[key] || event[FILTER_DIMENSIONS[key]] || null;

      if (template[key] !== valueInEvent) {
        return false;
      }
    }
  }

  return true;
}

/**
 * find event that matches template, if exists
 * @param {Array} dataSourceData
 * [
 *   {
 *     "timestamp": "2017-04-27T07:00:00.000Z",
 *     "event": {
 *       "user_data_platform": "ANDROID_APP",
 *       "from_desktop": "false",
 *       "total_count": 66
 *     }
 *   },
 *   [...]
 * ]
 * @param {Object} dimensionTemplate
 * {
 *   "user_data_platform": "ANDROID_APP",
 *   "from_desktop": "false"
 * }
 * @return {Array} list of events that match the dimension template
 */
export function getEvents(dataSourceData: GroupByResult[], dimensionTemplate: Record<string, any>): Record<string, any>[] {
  if (!dataSourceData || !dataSourceData.length) {
    return [];
  }

  const data = dataSourceData.filter(checkDimensionMatch.bind(null, dimensionTemplate));

  if (!data) {
    return [];
  }

  return data.map((item) => item.event || {});
}

/**
 * find events that match dimension template given data from one to many datasources,
 * then sum the total_count
 * @param {Array} dataSources
 * [
 *  [ { timestamp: <Date>, event: { [...] } } ],
 *  [...]
 * ]
 * @param {string} metric
 * @param {Object} dimensionTemplate
 * {
 *   "user_data_platform": "ANDROID_APP",
 *   "from_desktop": "false"
 * }
 * @return {Number}
 */
export function getEventCount(
  dataSources: GroupByResult[][] = [],
  metric: string = 'total_count',
  dimensionTemplate: Record<string, any> = {}
): number {
  if (!dataSources || !dataSources.length) {
    return 0;
  }

  return dataSources.reduce((acc: number, dataSourceData: GroupByResult[]) => {
    if (!dataSourceData) {
      return acc;
    }

    const events = getEvents(dataSourceData, dimensionTemplate);
    return (
      acc +
      events.reduce((eventCount, event) => {
        // TODO remove after DATA-1440 complete
        return eventCount + parseFloat(event[metric] || 0);
      }, 0)
    );
  }, 0);
}
export const getDimensionValueHash = (dimensions: Array<string>, entry: Record<string, any>) => {
  return dimensions.reduce((hash, dimension) => {
    const value = dimension === 'timestamp' ? entry.timestamp : entry.event[dimension] || UNPOPULATED_VALUE;

    if (!hash.length) {
      return `${value}`;
    }

    return `${hash}-${value}`;
  }, '');
};

/**
 * Get all values for the given entry.
 * If value is non zero and empty, set it to null explicitly
 * We need empty string "" to be set to null rather than leaving it
 * as an empty string
 * @param  {Array<string>} dimensions
 * @param  {Object} entry
 */
export const getDimensionValueObj = (dimensions: Array<string>, entry: Record<string, any>): Record<string, any> => {
  return dimensions.reduce((acc, dimension) => {
    if (dimension === 'timestamp') {
      return R.assoc('timestamp', entry.timestamp, acc);
    }

    return R.assoc(
      dimension,
      entry.event[dimension] === 0 || entry.event[dimension] === '0'
        ? entry.event[dimension]
        : entry.event[dimension] || UNPOPULATED_VALUE,
      acc
    );
  }, {});
};

/**
 * @param {Array} dimensions=[]
 * [ 'user_data_platform', 'last_attributed_touch_data_tilde_campaign']
 * @param {Object} dataSources DataSource Payload={}
 * @return {Array} rows
 */
export const initializeRowsWithDimensionValues = (dimensions: string[] = [], dataSources: GroupByResponse = {}): Rows => {
  if (!dimensions || !dataSources) {
    return [];
  }

  const dimensionValueMap = {};

  Object.values(dataSources).forEach((dataSource) => {
    if (dataSource && Array.isArray(dataSource)) {
      dataSource.forEach((entry) => {
        const hash = getDimensionValueHash(dimensions, entry);

        if (!dimensionValueMap[hash]) {
          dimensionValueMap[hash] = getDimensionValueObj(dimensions, entry);
        }

        // Not a great pattern to mutate the dataSources object, but for performance reasons will save us from going through another time
        if (dimensions.includes('timestamp')) {
          entry.event.timestamp = entry.timestamp;
        }
      });
    }
  });

  return Object.values(dimensionValueMap);
};

/*
 * method that generates a hash key. The hash key associates a data source with a table row.
 * i.e. If we want to associate all events from the eo_install datasource who's dimensions are the following:
 * {
    last_attributed_touch_data_tilde_advertising_partner_name: 'Facebook',
    secondary_publisher: 'NBA.com'
 * }
 * this method would return a hash key that is eo_install-["Facebook", "NBA.com"]
 */
export function generateHashKey(data: Record<string, any>, dataSource: string, dimensions: Array<string>): string {
  const toHash = {};
  dimensions.forEach((dimension) => {
    // [EG-3047] use 'null' for fraud_codes and fraud_names for unblocked fraud datasources
    const dimensionIsFraudValue = dimension === 'fraud_codes' || dimension === 'fraud_names';

    if (dimensionIsFraudValue && UNBLOCKED_FRAUD_DATASOURCES.includes(dataSource)) {
      toHash[dimension] = null;
    } else {
      // treat empty, null, and undefined dimensions in the same way
      toHash[dimension] = data[dimension] || UNPOPULATED_VALUE;
    }
  });
  return `${dataSource}-${serialize(toHash)}`;
}

/*
 * method that contructs an object where the keys are an association of data sources and dimension names/values (see the explanation in generateHashKey)
 * and the values are the druid events that match up to the data source and dimension in the key.
 * For Example:
 *
 * dataSources -
 * [{
 *   "version": "v1",
 *   "timestamp": "2017-04-27T07:00:00.000Z",
 *   "event": {
 *     "user_data_platform": "IOS_APP",
 *     "from_desktop": "false",
 *     "total_count": 569
 * }]
 *
 * dimensions -
 * [
    'user_data_platform',
    'from_desktop'
 * ]
 *
 * rows -
 * {
 *   "user_data_platform": "IOS_APP",
 *   "from_desktop": "false",
 * }
 *
 * return -
 * {
 *   'eo_install-["IOS_APP", "false"]': [{
 *      "version": "v1",
 *      "timestamp": "2017-04-27T07:00:00.000Z",
 *      "event": {
 *        "user_data_platform": "IOS_APP",
 *        "from_desktop": "false",
 *        "total_count": 569
 *    }]
 * }
 */
export function generateRowAndDataSourceHash(dataSources: GroupByResponse, dimensions: Array<string>): Row {
  return reduceObj(
    (acc, events, dataSource) => {
      if (!events || !Array.isArray(events)) {
        return acc;
      }

      const currEvents = events.reduce((hashMap, dataSourceData) => {
        const hash = generateHashKey(dataSourceData.event, dataSource, dimensions);

        if (hashMap[hash]) {
          hashMap[hash] = hashMap[hash].concat(dataSourceData.event);
          return hashMap;
        }

        hashMap[hash] = [dataSourceData.event];
        return hashMap;
      }, {});

      return Object.assign(acc, currEvents);
    },
    {},
    dataSources
  );
}

function _fallbackTransformer(columnKey: string): (arg0: Record<string, any>) => Record<string, any> {
  return function ({ event = {}, metric = 'total_count', row = {} }) {
    const count = appendNumericMetricWithEventData(row[columnKey], event[metric]);
    return {
      [columnKey]: count,
      ...getMultipleCampaignAssociationsIfApplicable(event, columnKey),
    };
  };
}

function getMultipleCampaignAssociationsIfApplicable(event: Event, dataSourceKey: string): Record<string, any> {
  if (EO_DATASOURCES.includes(dataSourceKey)) {
    return {
      [MULTIPLE_CAMPAIGNS_ASSOCIATIONS]: event[MULTIPLE_CAMPAIGNS_ASSOCIATIONS],
    };
  }

  return {};
}

/**
 * @param {Array} initialized rows
 * @param {Array} dimensions=[]
 * @param {Object} Druid DataSource Payload
 * @param {string} metric we want to pull off the event, passed to transformers
 * (i.e. 'total_count' or 'unique_count')
 * @param {Object} [transformers={}]
 * @return {Array} rows
 */
export function hydrateRowsFromDataSources(
  rows: Rows,
  dimensions: string[],
  dataSources: GroupByResponse,
  metric: string = 'total_count',
  transformers: Record<string, any> = {}
): Rows {
  if (!rows || !dimensions) {
    return [];
  }

  if (!dataSources) {
    return rows;
  }

  const rowAndDataSourceHash = generateRowAndDataSourceHash(dataSources, dimensions);
  return rows.map((rowTemplate) => {
    let row = rowTemplate;
    Object.keys(dataSources).forEach((dataSourceKey) => {
      const hash = generateHashKey(row, dataSourceKey, dimensions);
      const events = rowAndDataSourceHash[hash];

      const transformer =
        transformers[dataSourceKey] ||
        STANDARD_DATASOURCE_TRANSFORMERS[dataSourceKey] ||
        STANDARD_BLOCKED_DATASOURCE_TRANSFORMERS[dataSourceKey] ||
        COST_TRANSFORMER[dataSourceKey] ||
        _fallbackTransformer(dataSourceKey);

      if (!events) {
        row = Object.assign(
          row,
          transformer({
            event: {},
            metric,
            dimensions,
            row,
          })
        );
      } else {
        row = events.reduce((newRow, event = {}) => {
          const cellData = transformer({
            event,
            metric,
            dimensions: dimensions,
            row: newRow,
          });
          return Object.assign(newRow, cellData);
        }, row);
      }
    });
    return row;
  });
}

/**
 * Removes rows that are empty for all columns
 */
export function trimEmptyRows(rows: Rows, dimensions: string[]): Record<string, any>[] {
  return rows.filter((row) => {
    const rowWithoutDimensions = R.omit(dimensions, row);
    const rowValues = R.values(rowWithoutDimensions);
    const allEmpty = R.all((val) => !val, rowValues);
    return !allEmpty;
  });
}

/**
 * Generate rows from druid payloads keyed to corresponding to data sources, and
 * the specified dimensions. Optionally you can override transformers per data source
 * @param {Array} dimensions=[]
 * [ 'user_data_platform', 'last_attributed_touch_data_tilde_campaign']
 * @param {Object} Druid DataSource Payload={}
 * @param {string} metric we want to pull off the event, passed to transformers
 * (i.e. 'total_count' or 'unique_count')
 * @param {Object} [transformers={}]
 *{
 *  eo_install: function(event) { [...] }
 *}
 * @return {Array} rows
 */
export const populateRowsFromGroupBy = (
  dimensions: string[] = [],
  dataSources: GroupByResponse = {},
  metric: string = 'total_count',
  transformers: Record<string, any> = {}
): Record<string, any>[] => {
  const rows = initializeRowsWithDimensionValues(dimensions, dataSources);
  const hydratedRows = hydrateRowsFromDataSources(rows, dimensions, dataSources, metric, transformers);
  return trimEmptyRows(hydratedRows, dimensions);
};
export function mergeGroupByResponses(
  groupByResponse1: GroupByResponse,
  groupByResponse2: GroupByResponse
): GroupByResponse {
  const merged = Object.keys(groupByResponse1).reduce((acc, dataSource) => {
    if (acc[dataSource]) {
      return { ...acc, [dataSource]: [...acc[dataSource], ...groupByResponse1[dataSource]] };
    }

    return { ...acc, [dataSource]: groupByResponse1[dataSource] };
  }, groupByResponse2);
  return merged;
}

export function flattenAllGroupByResponses(data: GroupByResponse = {}) {
  return Object.values(data).reduce<GroupByResult[]>((flatResponses, response) => {
    if (!response || !Array.isArray(response)) {
      return flatResponses;
    }

    return [...flatResponses, ...response];
  }, []);
}
